import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, getDocs, where, orderBy, Timestamp, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const Earn = () => {
  const { wallets } = useWallet();
  const { currentUser } = useAuth();
  const walletA = wallets?.walletA || 0;
  const [blogPosts, setBlogPosts] = useState([]);
  const [googleSearchBlogs, setGoogleSearchBlogs] = useState([]);
  const [claimedBlogs, setClaimedBlogs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hideClaimedBlogs, setHideClaimedBlogs] = useState(true);
  const [activeTab, setActiveTab] = useState('rewards');
  const [googleSearchClaimed, setGoogleSearchClaimed] = useState({});
  const [googleSearchUrl, setGoogleSearchUrl] = useState('https://www.google.com/search?q=uptodatefinance.com+credit+card');
  const navigate = useNavigate();
  
  // Check which blogs have been claimed since the last reset (midnight at 12:00)
  useEffect(() => {
    const checkClaimedBlogs = async () => {
      if (!currentUser) return;
      
      try {
        console.log("Initial claim check...");
        // Get the timestamp for the last midnight (00:00)
        const now = new Date();
        const lastMidnight = new Date(now);
        lastMidnight.setHours(0, 0, 0, 0); // Set to 00:00:00.000
        
        // Query for direct blog claims made since midnight
        const claimsRef = collection(db, 'users', currentUser.uid, 'rewardClaims');
        const directClaimsQuery = query(
          claimsRef,
          where('type', '==', 'blog'),
          where('claimedAt', '>=', Timestamp.fromDate(lastMidnight))
        );
        
        // First do a broader check to see ALL claims for debugging
        const allClaimsQuery = query(
          claimsRef,
          where('claimedAt', '>=', Timestamp.fromDate(lastMidnight))
        );
        
        const allClaimsSnap = await getDocs(allClaimsQuery);
        console.log(`Found ${allClaimsSnap.size} total claims of any type`);
        allClaimsSnap.forEach(doc => {
          const data = doc.data();
          console.log(`Raw claim found - Type: ${data.type}, BlogID: ${data.blogId}, ClaimedAt: ${data.claimedAt?.toDate()}`);
        });
        
        // Execute query
        const directSnapshot = await getDocs(directClaimsQuery);
        
        console.log(`Found ${directSnapshot.size} direct blog claims`);
        
        // Process direct claims
        const claimedBlogsMap = {};
        const googleClaimedMap = {};
        directSnapshot.forEach((doc) => {
          const data = doc.data();
          claimedBlogsMap[data.blogId] = true;
          console.log(`Direct claim found for blog: ${data.blogId}`);
          
          // Check if this is an uptodatefinance.com blog or Google search claim
          if (data.blogId && 
             (data.blogId.includes('.html') || 
              data.blogId.includes('uptodatefinance') ||
              data.blogId.includes('google-search'))) {
            console.log('Found a claimed Google search blog');
            
            // Track the specific Google search blog ID if available
            if (data.googleSearchId) {
              googleClaimedMap[data.googleSearchId] = true;
              console.log(`Tracking specific Google search claim for ID: ${data.googleSearchId}`);
            } else if (data.blogId.startsWith('google-search-')) {
              // Handle both formats - old (google-search-generic) and new (google-search-blog-xxx)
              let googleSearchId;
              
              if (data.blogId.startsWith('google-search-blog-')) {
                // New format
                googleSearchId = data.blogId.replace('google-search-blog-', '');
                googleClaimedMap[googleSearchId] = true;
                console.log(`Tracking specific blog from Google search: ${googleSearchId}`);
              } else {
                // Old format
                googleSearchId = data.blogId.replace('google-search-', '');
                if (googleSearchId !== 'generic') {
                  googleClaimedMap[googleSearchId] = true;
                  console.log(`Tracking Google search claim from blogId: ${googleSearchId}`);
                }
              }
            }
          }
        });
        
        setClaimedBlogs(claimedBlogsMap);
        setGoogleSearchClaimed(googleClaimedMap);
        
        // Also check localStorage for client-side claimed blogs
        try {
          const claimedBlogsStr = localStorage.getItem('claimedBlogs');
          if (claimedBlogsStr) {
            const localClaims = JSON.parse(claimedBlogsStr);
            // Get today's date in YYYY-MM-DD format
            const today = new Date();
            const dateStr = today.getFullYear() + '-' + 
                           String(today.getMonth() + 1).padStart(2, '0') + '-' +
                           String(today.getDate()).padStart(2, '0');
            
            // If there are claims for today, add them to our map
            if (localClaims[dateStr]) {
              const updatedClaims = {...claimedBlogsMap};
              const updatedGoogleClaims = {...googleClaimedMap};
              
              // Process each local claim
              localClaims[dateStr].forEach(blogId => {
                updatedClaims[blogId] = true;
                
                // Check if this is a Google search blog claim
                if (blogId.startsWith('google-search-')) {
                  // Handle both formats
                  let googleSearchId;
                  
                  if (blogId.startsWith('google-search-blog-')) {
                    // New format: blog-specific ID
                    googleSearchId = blogId.replace('google-search-blog-', '');
                    updatedGoogleClaims[googleSearchId] = true;
                    console.log(`Found blog-specific Google search claim: ${googleSearchId}`);
                  } else {
                    // Old format
                    googleSearchId = blogId.replace('google-search-', '');
                    if (googleSearchId !== 'generic') {
                      updatedGoogleClaims[googleSearchId] = true;
                      console.log(`Found Google search claim in localStorage for ID: ${googleSearchId}`);
                    }
                  }
                }
              });
              
              setClaimedBlogs(updatedClaims);
              setGoogleSearchClaimed(updatedGoogleClaims);
            }
          }
        } catch (localErr) {
          console.error('Error parsing localStorage claimed blogs:', localErr);
        }
        
      } catch (err) {
        console.error('Error checking claimed blogs:', err);
      }
    };
    
    checkClaimedBlogs();
    
    // Set up an interval to check if we've passed midnight
    const checkMidnightInterval = setInterval(() => {
      const now = new Date();
      // If it's 12:00 AM (or very shortly after), refresh the claims
      if (now.getHours() === 0 && now.getMinutes() === 0) {
        checkClaimedBlogs();
      }
    }, 60000); // Check every minute
    
    return () => {
      clearInterval(checkMidnightInterval);
    };
  }, [currentUser]);
  
  // Fetch active blog posts from Firestore
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setLoading(true);
        
        // Fetch Google search blogs
        try {
          const googleBlogsRef = collection(db, 'googleSearchBlogs');
          const googleBlogsQuery = query(
            googleBlogsRef, 
            where('active', '==', true),
            orderBy('createdAt', 'desc')
          );
          
          const googleBlogsSnap = await getDocs(googleBlogsQuery);
          const googleBlogs = [];
          
          googleBlogsSnap.forEach((doc) => {
            googleBlogs.push({
              id: doc.id,
              ...doc.data(),
              isGoogleSearch: true
            });
          });
          
          setGoogleSearchBlogs(googleBlogs);
          console.log(`Loaded ${googleBlogs.length} Google search blogs`);
          
          // Debug: Log the structure of each Google search blog
          googleBlogs.forEach(blog => {
            console.log(`Google search blog: ID=${blog.id}, Title=${blog.title}, URL=${blog.searchUrl}`);
          });
        } catch (googleErr) {
          console.error('Error loading Google search blogs:', googleErr);
        }
        
        // Fetch regular blogs
        const blogsRef = collection(db, 'blogRewards');
        const q = query(
          blogsRef, 
          where('active', '==', true),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const posts = [];
        
        querySnapshot.forEach((doc) => {
          posts.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setBlogPosts(posts);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBlogPosts();
  }, []);
  
  // Function to handle blog click
  const handleBlogClick = (blog) => {
    const blogId = blog.id;
    const urlBlogId = extractBlogIdFromUrl(blog.url);
    
    // Skip if blog is already claimed
    if (claimedBlogs[blogId] === true || claimedBlogs[urlBlogId] === true) {
      console.log(`Prevented click on claimed blog ${blogId}`);
      return;
    }
    
    // Set origin flag in localStorage to track that user is coming from our site
    try {
      localStorage.setItem('gpb_origin', 'true');
      // Set a timestamp to track when the user visited our site
      localStorage.setItem('gpb_origin_time', Date.now().toString());
      console.log('Set gpb_origin flag in localStorage for tracking multi-step flow');
    } catch (e) {
      console.error('Error setting localStorage origin flag:', e);
    }
    
    // Add a from=earn parameter to distinguish traffic source
    const blogUrl = new URL(blog.url);
    blogUrl.searchParams.set('code', blog.code);
    blogUrl.searchParams.set('from', 'earn');
    blogUrl.searchParams.set('gpb_origin', 'true'); // Add URL parameter as backup
    
    window.open(blogUrl.toString(), '_blank');
  };
  
  // Function to manually refresh claimed blogs status
  const refreshClaimedStatus = async () => {
    if (!currentUser) return;
    
    try {
      console.log("Starting claim refresh...");
      
      // Show feedback to user that refresh is happening
      alert("Refreshing blog status...");
      
      // Get the timestamp for the last midnight (00:00)
      const now = new Date();
      const lastMidnight = new Date(now);
      lastMidnight.setHours(0, 0, 0, 0); // Set to 00:00:00.000
      
      // Query for direct blog claims
      const claimsRef = collection(db, 'users', currentUser.uid, 'rewardClaims');
      const claimsQuery = query(
        claimsRef,
        where('type', '==', 'blog'),
        where('claimedAt', '>=', Timestamp.fromDate(lastMidnight))
      );
      
      // Execute query
      const snapshot = await getDocs(claimsQuery);
      
      console.log(`Found ${snapshot.size} blog claims`);
      
      // Process claims
      const claimedBlogsMap = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        claimedBlogsMap[data.blogId] = true;
        console.log(`Claim found for blog: ${data.blogId}`);
      });
      
      setClaimedBlogs(claimedBlogsMap);
      
      // Also check localStorage for client-side claimed blogs
      try {
        const claimedBlogsStr = localStorage.getItem('claimedBlogs');
        if (claimedBlogsStr) {
          const localClaims = JSON.parse(claimedBlogsStr);
          // Get today's date in YYYY-MM-DD format
          const today = new Date();
          const dateStr = today.getFullYear() + '-' + 
                         String(today.getMonth() + 1).padStart(2, '0') + '-' +
                         String(today.getDate()).padStart(2, '0');
          
          // If there are claims for today, add them to our map
          if (localClaims[dateStr]) {
            console.log(`Found ${localClaims[dateStr].length} direct claims in localStorage`);
            localClaims[dateStr].forEach(blogId => {
              claimedBlogsMap[blogId] = true;
              console.log(`Local claim found for blog: ${blogId}`);
            });
            setClaimedBlogs({...claimedBlogsMap});
          }
        }
      } catch (localErr) {
        console.error('Error parsing localStorage claimed blogs:', localErr);
      }
    } catch (err) {
      console.error('Error refreshing claimed blogs:', err);
    }
  };
  
  // Check for claimed blogs in localStorage more frequently
  useEffect(() => {
    const checkLocalStorageTimer = setInterval(() => {
      try {
        const claimedBlogsStr = localStorage.getItem('claimedBlogs');
        if (claimedBlogsStr) {
          const localClaims = JSON.parse(claimedBlogsStr);
          const today = new Date();
          const dateStr = today.getFullYear() + '-' + 
                         String(today.getMonth() + 1).padStart(2, '0') + '-' +
                         String(today.getDate()).padStart(2, '0');
          
          // Check for Google search blog claims
          if (localClaims[dateStr]) {
            // Track individual Google search blog claims
            const updatedGoogleClaims = {...googleSearchClaimed};
            let hasGoogleClaimChanges = false;
            
            // Process each localStorage claim
            localClaims[dateStr].forEach(blogId => {
              // Check if this is a Google search blog claim
              if (blogId.startsWith('google-search-')) {
                // Handle both formats
                let googleSearchId;
                
                if (blogId.startsWith('google-search-blog-')) {
                  // New format: blog-specific ID
                  googleSearchId = blogId.replace('google-search-blog-', '');
                  if (!updatedGoogleClaims[googleSearchId]) {
                    updatedGoogleClaims[googleSearchId] = true;
                    hasGoogleClaimChanges = true;
                    console.log(`Found new blog-specific Google search claim: ${googleSearchId}`);
                  }
                } else {
                  // Old format
                  googleSearchId = blogId.replace('google-search-', '');
                  if (googleSearchId !== 'generic' && !updatedGoogleClaims[googleSearchId]) {
                    updatedGoogleClaims[googleSearchId] = true;
                    hasGoogleClaimChanges = true;
                    console.log(`Found new Google search claim in localStorage for ID: ${googleSearchId}`);
                  }
                }
              }
            });
            
            if (hasGoogleClaimChanges) {
              setGoogleSearchClaimed(updatedGoogleClaims);
            }
            
          // Update direct claims
          if (localClaims[dateStr]) {
            const newClaims = {...claimedBlogs};
            let hasChanges = false;
            
            localClaims[dateStr].forEach(blogId => {
              if (!newClaims[blogId]) {
                newClaims[blogId] = true;
                hasChanges = true;
              }
            });
            
            if (hasChanges) {
              console.log('Updating claimed blogs from localStorage:', newClaims);
              setClaimedBlogs(newClaims);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error checking localStorage claimed blogs:', err);
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(checkLocalStorageTimer);
  }, [claimedBlogs, googleSearchClaimed]);
  
  // Helper function to extract blog ID from URL
  const extractBlogIdFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      return pathParts[pathParts.length - 1] || '';
    } catch (err) {
      console.error('Invalid URL:', url, err);
      return '';
    }
  };
  
  // Check if blog is claimed
  const isBlogClaimed = (blog) => {
    // Check by blog ID
    if (claimedBlogs[blog.id]) {
      return true;
    }
    
    // Check by URL
    try {
      const urlBlogId = extractBlogIdFromUrl(blog.url);
      if (urlBlogId && claimedBlogs[urlBlogId]) {
        return true;
      }
    } catch (err) {
      console.error('Error checking URL claim status:', err);
    }
    
    return false;
  };
  
  // Check if a URL is claimed
  const isBlogClaimedByUrl = (url) => {
    const urlBlogId = extractBlogIdFromUrl(url);
    return claimedBlogs[urlBlogId] === true;
  };
  
  // Connect wallet
  const connectWalletHandler = () => {
    if (!currentUser) {
      navigate('/login', { state: { returnUrl: '/earn' } });
    }
  };

  // Function to handle Google search link click
  const handleGoogleSearchClick = (googleSearchBlog) => {
    if (!currentUser) {
      connectWalletHandler();
      return;
    }
    
    // Set flag in localStorage to track that user clicked the Google search link
    try {
      // Mark that this visit originated from gameprebattle.com
      localStorage.setItem('gpb_origin', 'true');
      localStorage.setItem('gpb_origin_time', Date.now().toString());
      
      // Also track that they specifically clicked a Google search link
      localStorage.setItem('gpb_google_search_link', 'true');
      localStorage.setItem('gpb_google_search_id', googleSearchBlog.id);
      console.log('Set Google search flags in localStorage:', googleSearchBlog.id);
      
      // Verify the flags were set
      const checkFlag = localStorage.getItem('gpb_google_search_link');
      const checkId = localStorage.getItem('gpb_google_search_id');
      const checkOrigin = localStorage.getItem('gpb_origin');
      console.log('Verified Google search flags:', checkFlag, checkId, checkOrigin);
      
      // Wait a moment to ensure localStorage is updated before opening the window
      setTimeout(() => {
        // Open Google search in a new tab using the blog's search URL
        window.open(googleSearchBlog.searchUrl, '_blank');
      }, 100);
    } catch (e) {
      console.error('Error setting localStorage Google search flags:', e);
      // Fall back to just opening the search
      window.open(googleSearchBlog.searchUrl, '_blank');
    }
  };

  // Render blog cards
  const renderBlogPosts = () => {
    // Debug: Log what's in the googleSearchClaimed object
    console.log("Current googleSearchClaimed state:", googleSearchClaimed);
    console.log("Current claimedBlogs state:", claimedBlogs);
    
    // Create a list of all blog items including both regular blogs and Google search blogs
    const googleSearchCards = googleSearchBlogs.map(blog => {
      // Check if this specific blog has been claimed 
      // using various matching methods
      let isClaimed = false;
      
      // Check exact matches first
      if (googleSearchClaimed[blog.id] === true || 
          claimedBlogs[`google-search-blog-${blog.id}`] === true) {
        console.log(`Blog ${blog.id} is claimed via exact ID match`);
        isClaimed = true;
      }
      
      // If not claimed yet, check partial matches
      if (!isClaimed) {
        // Check if any key in googleSearchClaimed is contained within the blog title or ID
        const blogIdLower = blog.id.toLowerCase();
        const blogTitleLower = blog.title ? blog.title.toLowerCase() : '';
        
        // Check through all keys in googleSearchClaimed
        for (const key in googleSearchClaimed) {
          if (googleSearchClaimed[key] === true) {
            const keyLower = key.toLowerCase();
            
            // Check if this key is part of the blog ID or title
            if (blogIdLower.includes(keyLower) || keyLower.includes(blogIdLower) ||
                (blogTitleLower && (blogTitleLower.includes(keyLower) || keyLower.includes(blogTitleLower)))) {
              console.log(`Blog ${blog.id} is claimed via partial match with key: ${key}`);
              isClaimed = true;
              break;
            }
          }
        }
      }
      
      // If still not claimed, check for URL-based claims
      if (!isClaimed && blog.searchUrl) {
        for (const key in claimedBlogs) {
          if (key.includes(blog.id) || (blog.searchUrl && key.includes(blog.searchUrl))) {
            console.log(`Blog ${blog.id} is claimed via URL match with key: ${key}`);
            isClaimed = true;
            break;
          }
        }
      }
      
      return {
        ...blog,
        description: 'Click to open Google search, then visit the first blog post to earn coins!',
        claimed: isClaimed,
        isGoogleSearch: true
      };
    });
    
    const allBlogs = [
      // Add all Google search blogs
      ...googleSearchCards,
      // Add all regular blogs from the database
      ...blogPosts
    ];

    // Filter out claimed blogs if hideClaimedBlogs is true
    const filteredBlogs = hideClaimedBlogs 
      ? allBlogs.filter(blog => {
          if (blog.isGoogleSearch) {
            return !blog.claimed;
          } else {
            return !isBlogClaimed(blog);
          }
        }) 
      : allBlogs;

    if (filteredBlogs.length === 0) {
      return (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
          <p className="text-gray-600 dark:text-gray-300">
            {hideClaimedBlogs 
              ? "You've claimed all available blog rewards for today! Check back tomorrow for more." 
              : "No blog rewards available at the moment. Check back later!"}
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-4">
        {filteredBlogs.map(blog => {
          // For regular blogs
          const blogId = blog.isGoogleSearch ? blog.id : extractBlogIdFromUrl(blog.url);
          const isClaimed = blog.isGoogleSearch ? blog.claimed : isBlogClaimed(blog);
          
          return (
            <div 
              key={blog.id}
              onClick={() => {
                if (!isClaimed) {
                  if (blog.isGoogleSearch) {
                    handleGoogleSearchClick(blog);
                  } else {
                    handleBlogClick(blog);
                  }
                }
              }}
              className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 p-4 ${
                isClaimed ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750'
              }`}
            >
              {isClaimed && (
                <div className="absolute top-0 right-0 m-2 p-1 px-2 bg-blue-500 text-white text-xs font-bold rounded">
                  CLAIMED
                </div>
              )}
              <div className="flex flex-col">
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">{blog.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                    {blog.description || 'Read this article to earn coins!'}
                  </p>
                </div>
                <div className="flex justify-end items-center">
                  <div className="flex items-center">
                    <span className="text-yellow-500 mr-1">🪙</span>
                    <span className="font-bold">50</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Earn Coins</h1>
      
      {/* Wallet Balance */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6 flex items-center justify-between">
        <div>
          <span className="text-sm text-gray-500 dark:text-gray-400">Your Balance:</span>
          <div className="flex items-center">
            <span className="text-yellow-500 text-2xl mr-2">🪙</span>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{walletA.toLocaleString()}</span>
          </div>
        </div>
        {!currentUser && (
          <button
            onClick={connectWalletHandler}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Connect Wallet
          </button>
        )}
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          className={`py-2 px-4 font-medium text-sm ${
            activeTab === 'rewards'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('rewards')}
        >
          Blog Rewards
        </button>
      </div>
      
      {/* Toggle for hiding claimed blogs */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Blog Rewards</h2>
        <div className="flex items-center">
          <label className="inline-flex items-center cursor-pointer mr-4">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={hideClaimedBlogs}
              onChange={() => setHideClaimedBlogs(!hideClaimedBlogs)}
            />
            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Hide Claimed</span>
          </label>
          <button
            onClick={refreshClaimedStatus}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
          >
            Refresh Status
          </button>
        </div>
      </div>
      
      {/* Blog Posts (Combined) */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      ) : (
        renderBlogPosts()
      )}
    </div>
  );
};

export default Earn; 